/**
 * Argo OS — localStorage-backed storage adapter (dev fallback).
 *
 * Implements the same `Store` interface as the Firestore adapter in
 * `store.ts`, persisting each collection as a JSON array under a
 * `localStorage` key. This is what makes the app work in local
 * development with no Firebase config: `store.ts` selects this adapter
 * whenever `useFirestore` is false (see `src/lib/firebase.ts`).
 *
 * `subscribe()` is backed by an in-tab listener registry so views that
 * call `db.subscribe(...)` re-render immediately after a mutation, plus
 * a `storage` event listener so changes made in another tab propagate.
 * Records are stored exactly as the adapter layer (e.g. `tickets.ts`,
 * `accounts.ts`) hands them over — ids and timestamps are minted there,
 * not here, so this file has no dependency on `uuid`/`nowIso`.
 */

import type { CollectionName, CollectionTypes } from './types';
import type { Store } from './store';
import { emitStoreHealth, StoreQuotaError } from './storeHealth';

const PREFIX = 'argo-os:';
const keyFor = (name: CollectionName) => `${PREFIX}${name}`;

// --- Schema versioning harness --------------------------------------------
//
// Bumping SCHEMA_VERSION and adding an ordered entry to `migrations`
// gives field renames / shape changes a real, tested home so the
// corruption-quarantine path in read() stays the exception. The baseline
// is an honest no-op: v1 establishes the harness, it does not claim to
// reshape any existing data.
const SCHEMA_KEY = `${PREFIX}__schema`;
const SCHEMA_VERSION = 1;

/** Ordered migrations, applied for every version strictly above stored. */
const migrations: Record<number, () => void> = {
  // v1 — baseline. No-op: just stamps the harness into existence.
  1: () => {},
};

function runMigrations(): void {
  if (typeof localStorage === 'undefined') return;
  let stored = 0;
  try {
    const raw = localStorage.getItem(SCHEMA_KEY);
    stored = raw ? Number.parseInt(raw, 10) || 0 : 0;
  } catch {
    stored = 0;
  }
  if (stored >= SCHEMA_VERSION) return;
  for (let v = stored + 1; v <= SCHEMA_VERSION; v++) {
    migrations[v]?.();
  }
  try {
    localStorage.setItem(SCHEMA_KEY, String(SCHEMA_VERSION));
  } catch {
    // If we can't even stamp the version, the next boot retries; harmless.
  }
}

runMigrations();

type Listener = (records: unknown[]) => void;
const listeners = new Map<CollectionName, Set<Listener>>();

function read<C extends CollectionName>(name: C): CollectionTypes[C][] {
  let raw: string | null;
  try {
    raw = localStorage.getItem(keyFor(name));
  } catch {
    return [];
  }
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as CollectionTypes[C][]) : [];
  } catch {
    // Corruption: instead of silently dropping the trail (the old
    // behaviour), quarantine the bad blob under a timestamped key and
    // surface a warning. We still return [] so the app keeps running,
    // but the data is recoverable and the operator is told.
    try {
      const quarantineKey = `${PREFIX}__corrupt:${name}:${Date.now()}`;
      localStorage.setItem(quarantineKey, raw);
      localStorage.removeItem(keyFor(name));
    } catch {
      // Best-effort; if quarantine itself fails we still degrade safely.
    }
    emitStoreHealth({
      level: 'warning',
      scope: name,
      message: `Stored ${name} data was unreadable and was quarantined.`,
      detail: 'A backup copy was kept; the list was reset to continue.',
    });
    return [];
  }
}

function write<C extends CollectionName>(
  name: C,
  records: CollectionTypes[C][],
): void {
  try {
    localStorage.setItem(keyFor(name), JSON.stringify(records));
  } catch (err) {
    const isQuota =
      err instanceof DOMException &&
      (err.name === 'QuotaExceededError' ||
        // Firefox's legacy name for the same condition.
        err.name === 'NS_ERROR_DOM_QUOTA_REACHED');
    if (isQuota) {
      emitStoreHealth({
        level: 'error',
        scope: 'storage',
        message: 'Local storage is full — that change was not saved.',
        detail: `Could not persist ${name}. Free up space and retry.`,
      });
      // Rethrow typed so the calling mutation surfaces the failure
      // instead of pretending the write succeeded.
      throw new StoreQuotaError(
        name,
        `localStorage quota exceeded while writing "${name}"`,
      );
    }
    throw err;
  }
  const set = listeners.get(name);
  if (set) set.forEach((fn) => fn(records));
}

// Cross-tab propagation: when another tab writes one of our keys, notify
// any in-tab subscribers for that collection.
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (e) => {
    if (!e.key || !e.key.startsWith(PREFIX)) return;
    const name = e.key.slice(PREFIX.length) as CollectionName;
    const set = listeners.get(name);
    if (set) set.forEach((fn) => fn(read(name)));
  });
}

export const localDb: Store = {
  async list<C extends CollectionName>(name: C) {
    return read(name);
  },

  async get<C extends CollectionName>(name: C, id: string) {
    const found = read(name).find((r) => (r as { id: string }).id === id);
    return (found ?? null) as CollectionTypes[C] | null;
  },

  async add<C extends CollectionName>(name: C, record: CollectionTypes[C]) {
    const id = (record as { id: string }).id;
    const all = read(name);
    if (all.some((r) => (r as { id: string }).id === id)) {
      throw new Error(`[store-local] ${name}/${id} already exists`);
    }
    write(name, [...all, record]);
  },

  async put<C extends CollectionName>(name: C, record: CollectionTypes[C]) {
    const id = (record as { id: string }).id;
    const all = read(name).filter((r) => (r as { id: string }).id !== id);
    write(name, [...all, record]);
  },

  async update<C extends CollectionName>(
    name: C,
    id: string,
    patch: Partial<CollectionTypes[C]>,
  ) {
    const all = read(name);
    const idx = all.findIndex((r) => (r as { id: string }).id === id);
    if (idx === -1) {
      throw new Error(`[store-local] update target ${name}/${id} missing`);
    }
    const next = { ...all[idx], ...patch } as CollectionTypes[C];
    const copy = all.slice();
    copy[idx] = next;
    write(name, copy);
    return next;
  },

  async remove<C extends CollectionName>(name: C, id: string) {
    const all = read(name).filter((r) => (r as { id: string }).id !== id);
    write(name, all);
  },

  subscribe<C extends CollectionName>(
    name: C,
    fn: (records: CollectionTypes[C][]) => void,
  ) {
    let set = listeners.get(name);
    if (!set) {
      set = new Set();
      listeners.set(name, set);
    }
    const listener = fn as Listener;
    set.add(listener);
    // Emit the current snapshot immediately so callers don't have to
    // separately call list() on mount (matches Firestore onSnapshot).
    fn(read(name));
    return () => {
      set?.delete(listener);
    };
  },

  async replaceAll<C extends CollectionName>(
    name: C,
    records: CollectionTypes[C][],
  ) {
    write(name, records);
  },
};
